/**
 * @module components/Controls/ControlAssignmentModal
 * @description Modal para asignar referentes técnicos a un control ISO 27002.
 *
 * Muestra la lista de referentes activos con nombre, unidad y email. El usuario
 * puede seleccionar uno o más referentes (multi-selección). Al confirmar, llama
 * a `DataContext.assignControlToReferent()` que elimina asignaciones previas
 * e inserta las nuevas en `control_assignments`.
 *
 * @see DataContext.assignControlToReferent para la lógica de asignación en DB
 * @see ControlsTable que abre este modal con el botón de asignar referente
 */
import React, { useState } from 'react';
import { X, User, Mail, Building, Send, AlertCircle } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { ISOControl } from '../../types/iso';

interface ControlAssignmentModalProps {
  control: ISOControl;
  onClose: () => void;
  onAssign: (referentIds: string[]) => void;
}

export function ControlAssignmentModal({ control, onClose, onAssign }: ControlAssignmentModalProps) {
  const { technicalReferents } = useData();
  const [selectedReferentIds, setSelectedReferentIds] = useState<string[]>(
    (control.assignedReferents || []).map(r => r.id)
  );
  const [isAssigning, setIsAssigning] = useState(false);

  const activeReferents = technicalReferents.filter(r => r.isActive);

  const handleReferentToggle = (referentId: string) => {
    setSelectedReferentIds(prev => 
      prev.includes(referentId)
        ? prev.filter(id => id !== referentId)
        : [...prev, referentId]
    );
  };
  const handleAssign = async () => {
    if (selectedReferentIds.length === 0) return;
    
    setIsAssigning(true);
    try {
      onAssign(selectedReferentIds);
      onClose();
    } catch (error) {
      console.error('Error assigning control:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const selectedReferents = activeReferents.filter(r => selectedReferentIds.includes(r.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Asignar Control
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {control.code} - {control.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeReferents.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                No hay referentes técnicos disponibles
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Primero debes crear referentes técnicos en el panel de administración
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Seleccionar Referentes Técnicos (puede seleccionar múltiples)
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activeReferents.map((referent) => (
                    <label
                      key={referent.id}
                      className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedReferentIds.includes(referent.id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedReferentIds.includes(referent.id)}
                        onChange={() => handleReferentToggle(referent.id)}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          {referent.avatar ? (
                            <img
                              src={referent.avatar}
                              alt={referent.name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${referent.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}`}>
                              <User className="w-3 h-3 text-white" />
                            </div>
                          )}
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            {referent.name}
                          </h4>
                          {referent.role === 'admin' && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-medium">
                              Admin
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            <Mail className="w-3 h-3 mr-1" />
                            <span>{referent.email}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            <Building className="w-3 h-3 mr-1" />
                            <span>{referent.unit}</span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {referent.assignedControls.length} control{referent.assignedControls.length !== 1 ? 'es' : ''} asignado{referent.assignedControls.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {selectedReferents.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Send className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-blue-800 dark:text-blue-200 font-medium mb-1">
                        Notificación por Email
                      </p>
                      <p className="text-blue-700 dark:text-blue-300 text-xs">
                        Se enviará un email a: <strong>{selectedReferents.map(r => r.email).join(', ')}</strong> notificando 
                        la asignación del control y las tareas asociadas a cada referente.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {activeReferents.length > 0 && (
          <div className="flex space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleAssign}
              disabled={selectedReferentIds.length === 0 || isAssigning}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {isAssigning ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Asignando...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Asignar y Notificar</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}